import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import {
  Trophy, DollarSign,
  ArrowRight, Sparkles,
  Terminal, Package, Cast
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'

type Winner = 'cmdesk' | null

// Language-independent cell values (numbers, symbols) and winner flags.
// Word cells live in compare.json under categories.<key>.features[i], same order.
interface FixedRow {
  winner: Winner
  cmdesk?: string
  anydesk?: string
  teamviewer?: string
}

interface RowText {
  name: string
  cmdesk?: string
  anydesk?: string
  teamviewer?: string
}

interface CategoryText {
  title: string
  features: RowText[]
}

interface ComparisonRow {
  name: string
  cmdesk: string
  anydesk: string
  teamviewer: string
  winner: Winner
}

interface ComparisonCategory {
  key: string
  title: string
  features: ComparisonRow[]
}

const categoryKeys = ['performance', 'security', 'enterprise', 'pricing', 'features'] as const

const fixedRows: Record<(typeof categoryKeys)[number], FixedRow[]> = {
  performance: [
    { cmdesk: '<15ms', anydesk: '~30ms', teamviewer: '~45ms', winner: 'cmdesk' },
    { cmdesk: '60 FPS', anydesk: '30 FPS', teamviewer: '30 FPS', winner: 'cmdesk' },
    { cmdesk: '4K+', anydesk: '4K', teamviewer: '4K', winner: null },
    { winner: 'cmdesk' },
    { cmdesk: '✓', winner: 'cmdesk' },
  ],
  security: [
    { cmdesk: '256-bit AES', anydesk: '256-bit AES', teamviewer: '256-bit AES', winner: null },
    { cmdesk: '✓', anydesk: '✓', teamviewer: '✓', winner: null },
    { winner: 'cmdesk' },
    { cmdesk: '✓', anydesk: '✓', teamviewer: '✓', winner: null },
    { winner: 'cmdesk' },
  ],
  enterprise: [
    { cmdesk: '✓', anydesk: '✗', teamviewer: '✗', winner: 'cmdesk' },
    { cmdesk: '✓', anydesk: '✗', teamviewer: '✗', winner: 'cmdesk' },
    { cmdesk: '✓', anydesk: '✗', teamviewer: '✗', winner: 'cmdesk' },
    { cmdesk: '✓', anydesk: '✗', teamviewer: '✗', winner: 'cmdesk' },
    { cmdesk: '✓', anydesk: '✗', teamviewer: '✗', winner: 'cmdesk' },
    { cmdesk: '✓', winner: 'cmdesk' },
  ],
  pricing: [
    { winner: 'cmdesk' },
    { winner: 'cmdesk' },
    { winner: 'cmdesk' },
    { winner: 'cmdesk' },
    { winner: 'cmdesk' },
  ],
  features: [
    { cmdesk: '✓', anydesk: '✓', teamviewer: '✓', winner: null },
    { cmdesk: '✓', anydesk: '✓', teamviewer: '✓', winner: null },
    { cmdesk: '✓', anydesk: '✓', teamviewer: '✓', winner: null },
    { cmdesk: '✓', anydesk: '✓', teamviewer: '✓', winner: null },
    { cmdesk: '✓', anydesk: '✓', teamviewer: '✓', winner: null },
    { cmdesk: '✓', winner: 'cmdesk' },
  ],
}

const highlightIcons = [Terminal, Package, Cast, DollarSign]

function ComparisonTable({ category, index }: { category: ComparisonCategory; index: number }) {
  const { t } = useTranslation('compare')
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="mb-12"
    >
      <h2 className="text-2xl font-display font-bold text-white mb-6">{category.title}</h2>
      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 bg-white/5">
              <th className="text-left py-4 px-6 text-gray-400 font-medium">{t('table.feature')}</th>
              <th className="text-center py-4 px-6 text-primary font-semibold bg-primary/5">
                <div className="flex items-center justify-center gap-2">
                  CMDesk
                  <span className="px-2 py-0.5 bg-primary text-white text-xs rounded">{t('table.us')}</span>
                </div>
              </th>
              <th className="text-center py-4 px-6 text-gray-400 font-medium">AnyDesk</th>
              <th className="text-center py-4 px-6 text-gray-400 font-medium">TeamViewer</th>
            </tr>
          </thead>
          <tbody>
            {category.features.map((feature) => (
              <tr key={feature.name} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                <td className="py-4 px-6 text-white">{feature.name}</td>
                <td className={`py-4 px-6 text-center font-medium ${
                  feature.winner === 'cmdesk' ? 'text-success bg-success/5' : 'text-white'
                }`}>
                  {feature.cmdesk}
                </td>
                <td className="py-4 px-6 text-center text-gray-400">{feature.anydesk}</td>
                <td className="py-4 px-6 text-center text-gray-400">{feature.teamviewer}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}

export default function Compare() {
  const { t } = useTranslation(['compare', 'common'])
  const headerRef = useRef(null)
  const isHeaderInView = useInView(headerRef, { once: true })

  const highlightTexts = t('highlights', { returnObjects: true }) as { title: string; desc: string }[]
  const highlights = highlightIcons.map((icon, i) => ({ icon, ...highlightTexts[i] }))

  const comparisonData: ComparisonCategory[] = categoryKeys.map((key) => {
    const text = t(`categories.${key}`, { returnObjects: true }) as CategoryText
    const rows = Array.isArray(text.features) ? text.features : []
    return {
      key,
      title: text.title,
      features: fixedRows[key].map((fixed, i) => {
        const row = rows[i] ?? { name: '' }
        return {
          name: row.name,
          cmdesk: fixed.cmdesk ?? row.cmdesk ?? '',
          anydesk: fixed.anydesk ?? row.anydesk ?? '',
          teamviewer: fixed.teamviewer ?? row.teamviewer ?? '',
          winner: fixed.winner,
        }
      }),
    }
  })

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/compare"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.compare'), url: '/compare' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Trophy className="w-4 h-4" />
            {t('hero.badge')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            {t('hero.title')}{' '}
            <span className="text-gradient">{t('hero.titleAccent')}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('hero.text')}
          </p>
        </motion.div>

        {/* Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
        >
          {highlights.map((highlight, index) => (
            <motion.div
              key={highlight.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass rounded-xl p-6 text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <highlight.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{highlight.title}</h3>
              <p className="text-gray-400 text-sm">{highlight.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Comparison Tables */}
        {comparisonData.map((category, index) => (
          <ComparisonTable key={category.key} category={category} index={index} />
        ))}

        {/* Verdict */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-3xl p-8 md:p-12 text-center mt-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-success" />
            <span className="text-success font-medium">{t('verdict.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            {t('verdict.title')}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            {t('verdict.text')}
          </p>
          <Link
            to="/download"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-primary/25"
          >
            {t('verdict.cta')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </motion.div>
  )
}
